import 'react-day-picker/style.css';
import '../../themes/registration.scss';
import '../../themes/calendar.scss';

import moment from 'moment';
import { useMemo } from 'react';
import { DayPicker, type DayProps } from 'react-day-picker';
import { useUserSelector } from '~/hooks';
import { IUser } from '~/models';
import { HeadRow } from '../../components/Request/multiday/components/Rows/DateRow/HeadRow';
import { MonthSelection } from '../../components/Request/multiday/components/Rows/DateRow/MonthSelection';
import { convertDatesToTimezone } from '~/utils/DateUtils';

export interface ICalendarSelectionProps {
  /** Callback function when dates are selected, receives array of date strings in YYYY-MM-DD format */
  onSelect: (dates: string[]) => void;
  /** Callback function when maximum days limit is exceeded */
  onLimitExceeded?: () => void;
  /** Maximum number of days that can be selected */
  maxDaysToSelect?: number;
  /** Array of pre-selected dates in YYYY-MM-DD format */
  dates?: string[];
  /** Array of dates that should be excluded (disabled) from selection. */
  excluded?: Date[];
  /** Data attribute for testing purposes */
  'data-cy'?: string;
}

/**
 * CalendarSelection Component
 *
 * A multi-date selection calendar component with timezone handling and configurable limits.
 * Provides an interface for selecting multiple dates with validation and formatting.
 *
 * ⚠️  DEVELOPER NOTE: This component is currently located in commonComponents
 * but appears to be used only for multiday request date selection. Consider whether
 * this should be moved to a more specific location like:
 * - src/components/Request/multiday/components/CalendarSelection
 * - src/components/Request/shared/CalendarSelection
 *
 * If this is truly only used for Request/multiday context, it may not need
 * to be a shared component.
 *
 * @example
 * ```tsx
 * <CalendarSelection
 *   onSelect={(dates) => console.log(dates)}
 *   maxDaysToSelect={5}
 *   dates={['2024-01-01', '2024-01-02']}
 *   onLimitExceeded={() => alert('Too many dates')}
 *   data-cy="calendar-selection"
 * />
 * ```
 */
export const CalendarSelection = ({
  onSelect,
  onLimitExceeded,
  maxDaysToSelect,
  dates = [],
  excluded = [],
  'data-cy': dataCy,
}: ICalendarSelectionProps) => {
  const currentUser: IUser = useUserSelector();

  const fromMonth = new Date();

  const convertedDates = useMemo(() => {
    if (!dates?.length) return [];
    const tz = currentUser.time_zone || moment.tz.guess();
    return convertDatesToTimezone(dates, tz).map((date) => new Date(date));
  }, [dates, currentUser.time_zone]);

  const handleDateSelection = (dateArr: Date[] | undefined): void => {
    if (!dateArr) {
      onSelect([]);
      return;
    }

    const formattedDates = dateArr.map((date) => moment(date).format('YYYY-MM-DD'));

    if (maxDaysToSelect && formattedDates.length > maxDaysToSelect) {
      if (maxDaysToSelect === 1) {
        const fmtLastDate = formattedDates[formattedDates.length - 1];
        onSelect([fmtLastDate]);
        return;
      }
      onLimitExceeded?.();
      return;
    }

    onSelect(formattedDates);
  };

  return (
    <DayPicker
      data-cy={dataCy}
      data-testid="calendar-selection"
      mode="multiple"
      onSelect={handleDateSelection}
      selected={convertedDates}
      startMonth={fromMonth}
      components={{
        MonthCaption: MonthSelection,
        Weekdays: HeadRow,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        Day({ day, modifiers, ...tdProps }: DayProps) {
          return (
            <td
              {...tdProps}
              data-testid={`calendar-selection.days.${moment(day.date).format('YYYY-MM-DD')}`}
            />
          );
        },
      }}
      disabled={[
        {
          before: new Date(),
          after: moment().add(99, 'days').toDate(),
        },
        ...excluded,
      ]}
    />
  );
};
